/**
 * The app role the staff token of a Producer Admins or MCP Users member carries. The history API admits it only on a
 * dev Admin UI token.
 */
export const MAS_ROLE = 'Mcp.User';

export const MAS_PAGE_SIZE = 50;

/** The page sizes the list offers. */
export const MAS_PAGE_SIZES = [25, 50, 100];

/** The longest search text the history API takes. */
export const MAS_QUERY_MAX_LENGTH = 200;

/** Which side of a message the chosen user is on: either, the sender or the recipient. */
export type MasActorRole = 'any' | 'sender' | 'recipient';

/** By time, or by the sender's or the recipient's display name as stored with the message. */
export type MasSort = 'time' | 'sender' | 'recipient';

export type MasSortOrder = 'asc' | 'desc';

/** An outbox row's state: `DeliveryUnknown` holds every later row of its lane until an operator resolves it. */
export type MasDeliveryState = 'Pending' | 'Sending' | 'Delivered' | 'Failed' | 'DeliveryUnknown';

/** `actorId` is `tid:oid`. `displayName` is the name stored with the message, null where none was stored. */
export interface MasActorV1 {
  actorId: string;
  displayName: string | null;
}

/**
 * Who a message is for. `Person` is a message to a teammate, who can acknowledge it. `Team` is a message or a workstream
 * announcement to the whole team, and `Workstream` a message to everyone taking part in one workstream; no one
 * acknowledges either.
 */
export type MasAudience = 'Person' | 'Team' | 'Workstream';

export const MAS_AUDIENCES: readonly MasAudience[] = ['Person', 'Team', 'Workstream'];

export interface MasMessageSummaryV1 {
  messageId: string;
  repository: string;
  sender: MasActorV1;
  recipient: MasActorV1;
  audience: MasAudience;
  /** The first 280 characters of the text. */
  excerpt: string;
  createdAt: string;
  acknowledgedAt: string | null;
  /** The workstream the message is about or addressed to, null for a message about no workstream. */
  workstreamId: string | null;
  /** The one session of the recipient a `Person` message names, null when any of their sessions may read it. */
  recipientSessionId: string | null;
}

/**
 * POST `mas/v1/messages/search`. Omitted bounds mean the 7 days up to now; `role` applies with `actor`; `q` matches a
 * literal substring of the text, ignoring case. A later page sends back the `from` and `to` the first page resolved,
 * so every page slices one window.
 */
export interface MasSearchRequestV1 {
  from?: string;
  to?: string;
  actor?: string;
  role?: MasActorRole;
  repository?: string;
  q?: string;
  audience?: MasAudience;
  workstreamId?: string;
  sort: MasSort;
  order: MasSortOrder;
  pageNumber: number;
  pageSize: number;
}

/** A summary's range and filters: it reads every match in time order, so it takes no sort or page. */
export type MasSummarySearchV1 = Omit<MasSearchRequestV1, 'sort' | 'order' | 'pageNumber' | 'pageSize'>;

/** `from` and `to` are the bounds the server resolved, and `totalCount` counts every match within them. */
export interface MasSearchResponseV1 {
  items: MasMessageSummaryV1[];
  from: string;
  to: string;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

/**
 * The sending session's branch and client, and the model, effort level and plugin version it reported when it
 * registered; all are null on a message older than session attribution, and the last three on a session that
 * did not report them.
 */
export interface MasSenderV1 extends MasActorV1 {
  sessionId: string | null;
  branch: string | null;
  client: string | null;
  model: string | null;
  effort: string | null;
  pluginVersion: string | null;
}

/**
 * One outbox lane's latest row: the DM (`channel` null) or the summary in one channel, named by its registry key.
 * `blocked` is true while an earlier row of the lane is `DeliveryUnknown`.
 */
export interface MasDeliveryV1 {
  kind: string;
  channel: string | null;
  state: MasDeliveryState;
  code: string | null;
  queuedAt: string;
  finishedAt: string | null;
  blocked: boolean;
}

/** GET `mas/v1/messages/{messageId}`. */
export interface MasMessageV1 {
  messageId: string;
  repository: string;
  sender: MasSenderV1;
  recipient: MasActorV1;
  audience: MasAudience;
  text: string;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBySessionId: string | null;
  /** The ids in the text that name existing messages, lowercase, in text order. */
  references: string[];
  deliveries: MasDeliveryV1[];
  workstreamId: string | null;
  recipientSessionId: string | null;
}

/** A member a message can be filtered by; `displayName` falls back to the actor id where no name is stored. */
export interface MasMemberV1 {
  actorId: string;
  displayName: string;
}

/** GET `mas/v1/filters`: every member and repository a message can be found by. */
export interface MasFiltersResponseV1 {
  members: MasMemberV1[];
  repositories: string[];
}
